const b64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
const b64re = /^(?:[A-Za-z\d+\/]{4})*?(?:[A-Za-z\d+\/]{2}(?:==)?|[A-Za-z\d+\/]{3}=?)?$/

/**
 * Matches a string drawn entirely from the base64 alphabet, standard or URL-safe, with or
 * without trailing padding.
 *
 * This is a cheap pre-check rather than a full validity test — decoding still rejects a
 * well-formed alphabet with a bad length. Its purpose is to let callers reject input that
 * could never decode without paying to construct and catch an exception, which matters on
 * paths that run on every storage read.
 */
const base64AlphabetPattern = /^[A-Za-z0-9+/\-_]*={0,2}$/

/** @internal */
const atobShim = (string: string): string => {
    let result: any = ''

    try {
        // Attempt to use the default
        result = window.atob(string)
        if (typeof result === 'string') {
            return result
        }
    } catch {}
    result = ''

    // atob can work with strings with whitespaces, even inside the encoded part,
    // but only \t, \n, \f, \r and ' ', which can be stripped.
    string = String(string).replace(/[\t\n\f\r ]+/g, '')
    if (!b64re.test(string))
        throw new TypeError("Failed to execute 'atob' on 'Window': The string to be decoded is not correctly encoded.")

    // Adding the padding if missing, for simplicity
    string += '=='.slice(2 - (string.length & 3))
    let bitmap,
        r1,
        r2,
        i = 0
    for (; i < string.length; ) {
        bitmap =
            (b64.indexOf(string.charAt(i++)) << 18) |
            (b64.indexOf(string.charAt(i++)) << 12) |
            ((r1 = b64.indexOf(string.charAt(i++))) << 6) |
            (r2 = b64.indexOf(string.charAt(i++)))

        result +=
            r1 === 64
                ? String.fromCharCode((bitmap >> 16) & 255)
                : r2 === 64
                ? String.fromCharCode((bitmap >> 16) & 255, (bitmap >> 8) & 255)
                : String.fromCharCode((bitmap >> 16) & 255, (bitmap >> 8) & 255, bitmap & 255)
    }

    return result
}

/** @internal */
export const arrayBufferFromBase64 = (b64: string): Uint8Array => {
    const base64 = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')

    const rawData = atobShim(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
}

/** @internal */
export const arrayBufferToBase64 = (arrayBuffer: any): string => {
    return window
        .btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(arrayBuffer))))
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
}

/** @internal */
export const stringToBase64 = (str: string): string => {
    let encoded: string | undefined
    try {
        if ('TextEncoder' in self && typeof self.TextEncoder === 'function' && 'encode' in self.TextEncoder.prototype) {
            encoded = arrayBufferToBase64(new self.TextEncoder().encode(str))
        } else {
            encoded = window.btoa(str)
            if (typeof encoded !== 'string') {
                encoded = undefined
            }
        }
    } catch (err) {
        console.warn('Unable to encode base64 string', err)
    }

    return encoded ?? str
}

/**
 * Decodes a base64 string, accepting both the URL-safe and standard alphabets with or
 * without `=` padding.
 *
 * Returns `undefined` for input that is not valid base64 instead of throwing. Callers read
 * values that may never have been encoded in the first place — a stored value written before
 * encoding was introduced, or by another writer entirely — so undecodable input is an
 * expected outcome to branch on, not a fault to report. Callers that require valid base64
 * should use {@link arrayBufferFromBase64}, which still throws.
 *
 * @internal
 */
export const stringFromBase64 = (encoded: string): string | undefined => {
    if (!base64AlphabetPattern.test(encoded)) {
        return undefined
    }

    try {
        if ('TextDecoder' in self && typeof self.TextDecoder === 'function' && 'decode' in self.TextDecoder.prototype) {
            return new self.TextDecoder().decode(arrayBufferFromBase64(encoded))
        }

        return atobShim(encoded)
    } catch {
        return undefined
    }
}
